import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import styled from "styled-components";
import TPC_URL from "../../../utils/TPC_URL";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Switch,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_KO } from "material-react-table/locales/ko";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

function Processed() {
  const { group_code, project_code } = useParams();
  const [rowData, setRowData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [filterActive, setFilterActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [showOnlyNew, setShowOnlyNew] = useState(false);

  const [rowSelection, setRowSelection] = useState({});
  const [completedQuantities, setCompletedQuantities] = useState({});
  const [completedDates, setCompletedDates] = useState({});
  const [inputMode, setInputMode] = useState("batch");
  const [batchCompletedDate, setBatchCompletedDate] = useState(dayjs());

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyOrderNumber, setHistoryOrderNumber] = useState(null);

  // 기준 날짜 가져오기
  const fetchBaseDate = useCallback(async () => {
    try {
      const response = await axios.get(`${TPC_URL}/api/tpc/getBaseDate?group_code=${group_code}&project_code=${project_code}`);

      if (response.data && response.data.baseDate) {
        return response.data.baseDate; // "YYYY-MM-DD"
      } else {
        return dayjs().format("YYYY-MM-DD");
      }
    } catch (error) {
      console.error("기준 날짜 조회 실패:", error);
      return dayjs().format("YYYY-MM-DD");
    }
  }, [group_code, project_code]);

  // 컴포넌트 마운트 시 기준 날짜 설정
  useEffect(() => {
    async function initBaseDate() {
      const baseDate = await fetchBaseDate();
      setBatchCompletedDate(dayjs(baseDate));
    }
    initBaseDate();
  }, [fetchBaseDate]);

  const CompletedQuantityEditor = ({ row }) => {
    const [value, setValue] = useState(
      completedQuantities[row.original.order_number] || 0
    );

    const handleChange = (e) => {
      const newValue = parseInt(e.target.value) || 0;
      setValue(newValue);
      setCompletedQuantities((prev) => ({
        ...prev,
        [row.original.order_number]: newValue,
      }));
    };

    return (
      <TextField
        type="number"
        value={value}
        onChange={handleChange}
        size="small"
        InputProps={{
          inputProps: { min: 0 },
        }}
        sx={{
          width: "100%",
          "& .MuiInputBase-input": {
            textAlign: "center",
            padding: "4px 8px",
          },
        }}
      />
    );
  };

  const CompletedDateEditor = ({ row }) => {
    const [value, setValue] = useState(
      completedDates[row.original.order_number]
        ? dayjs(completedDates[row.original.order_number])
        : null
    );

    // 초기값 설정 (기준 날짜로)
    useEffect(() => {
      if (!value) {
        fetchBaseDate().then((baseDate) => {
          const initialDate = dayjs(baseDate);
          setValue(initialDate);
          setCompletedDates((prev) => ({
            ...prev,
            [row.original.order_number]: initialDate.format("YYYY-MM-DD"),
          }));
        });
      }
    }, []);

    const handleChange = (newValue) => {
      setValue(newValue);
      setCompletedDates((prev) => ({
        ...prev,
        [row.original.order_number]: newValue
          ? newValue.format("YYYY-MM-DD")
          : null,
      }));
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value}
          onChange={handleChange}
          format="YYYY/MM/DD"
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: "100%",
                "& .MuiInputBase-input": {
                  padding: "4px 8px",
                  textAlign: "center",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const handleOpenHistory = async (orderNumber) => {
    try {
      setHistoryOpen(true);
      setHistoryLoading(true);
      setHistoryError("");
      setHistoryRows([]);
      setHistoryOrderNumber(orderNumber);

      const res = await axios.get(`${TPC_URL}/api/tpc/getResHistory`, {
        params: {
          group_code,
          project_code,
          order_number: orderNumber,
        },
      });

      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        setHistoryRows(res.data);
      } else {
        setHistoryRows([]);
        setHistoryError("해당 주문에 대한 납품 실적이 없습니다.");
      }
    } catch (e) {
      console.error("내역 조회 중 오류:", e);
      setHistoryError("내역을 불러오는 중 오류가 발생했습니다.");
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setHistoryRows([]);
    setHistoryError("");
    setHistoryOrderNumber(null);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      // ✅ 순번
      {
        accessorKey: "index",
        header: "순번",
        size: 60,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => {
          return (
            <Box sx={{ textAlign: "center", fontWeight: 600 }}>
              {row.index + 1}
            </Box>
          );
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 주문 번호
      {
        accessorKey: "order_number",
        header: "주문 번호",
        size: 150,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ cell, row }) => {
          const isNew = row.original.is_new === 1;
          return (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 0.75 
              }}
            >
              {isNew && (
                <Chip 
                  label="NEW" 
                  size="small" 
                  color="error"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    minWidth: 42,
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
              )}
              <span>{cell.getValue()}</span>
            </Box>
          );
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 납기일
      {
        accessorKey: "local_due_date",
        header: "납기일",
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const noDueDate =
            value === null ||
            value === undefined ||
            value === "" ||
            value === "null";
          return (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: noDueDate ? "#ff8787" : "transparent",
              }}
            >
              {value}
            </Box>
          );
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ Material Grouping
      {
        accessorKey: "material_grouping",
        header: "Material Grouping",
        size: 180,
        enableSorting: false,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ Add.descr.4
      {
        accessorKey: "add_descr_4",
        header: "Add.descr.4",
        size: 150,
        enableSorting: false,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 생산 지시 수량
      {
        accessorKey: "production_quantity",
        header: "생산 지시 수량",
        size: 130,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? value.toLocaleString() : "";
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
    ];
  
    if (inputMode === "individual") {
      baseColumns.push(
        {
          accessorKey: "completed_quantity",
          header: "완료 수량",
          size: 130,
          enableSorting: false,
          enableColumnActions: false,
          Cell: ({ row }) => <CompletedQuantityEditor row={row} />,
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
        },
        {
          accessorKey: "completed_date",
          header: "완료 일시",
          size: 150,
          enableSorting: false,
          enableColumnActions: false,
          Cell: ({ row }) => <CompletedDateEditor row={row} />,
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
        }
      );
    }
  
    baseColumns.push(
      // ✅ 변경 내역
      {
        accessorKey: "change_text",
        header: "변경 내역",
        size: 200,
        grow: true,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return null;
          const parts = value.split(",");
          if (parts.length === 1) return <div>{value}</div>;
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: "1.2",
              }}
            >
              <div>{parts[0].trim()}</div>
              <div>{parts.slice(1).join(",").trim()}</div>
            </div>
          );
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 삭스 생산일정
      {
        accessorKey: "earliest_start_date",
        header: "삭스 생산일정",
        size: 130,
        enableSorting: false,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 등록일
      {
        accessorKey: "register_date",
        header: "등록일",
        size: 120,
        enableSorting: false,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 주문 수량
      {
        accessorKey: "order_item_quantity",
        header: "주문 수량",
        size: 110,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? value.toLocaleString() : "";
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ Header Text
      {
        accessorKey: "header_text",
        header: "Header Text",
        size: 120,
        enableSorting: false,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      // ✅ 내역
      {
        accessorKey: "history",
        header: "내역",
        size: 120,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleOpenHistory(row.original.order_number)}
            sx={{ minWidth: 90, height: 32, fontSize: '0.7rem' }}
          >
            내역보기
          </Button>
        ),
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      }
    );
  
    return baseColumns;
  }, [inputMode, completedQuantities, completedDates]);
        
  const selectedRows = useMemo(() => {
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => filteredData.find((row) => row.order_number === key))
      .filter((row) => row !== undefined);
  }, [rowSelection, filteredData]);

  const handleCompleteOrders = async () => {
    try {
      const direction = inputMode === "batch" ? 0 : 1;
      const info = [];
  
      if (inputMode === "batch") {
        if (selectedRows.length === 0) {
          setSnackbar({
            open: true,
            message: "완료 처리할 주문을 선택해주세요",
            severity: "warning",
          });
          return;
        }
        const completeDate = batchCompletedDate.format("YYYY-MM-DD");
        selectedRows.forEach((row) => {
          info.push({
            order_number: row.order_number,
            production_quantity: row.production_quantity,
            complete_qty: row.production_quantity,
            complete_date: completeDate,
            item_code: row.add_descr_4, // ✅ 추가
          });
        });
      } else {
        filteredData.forEach((row) => {
          const completedQty = completedQuantities[row.order_number] || 0;
          const completedDate =
            completedDates[row.order_number] || dayjs().format("YYYY-MM-DD");
          if (completedQty > 0) {
            info.push({
              order_number: row.order_number,
              production_quantity: row.production_quantity,
              complete_qty: completedQty,
              complete_date: completedDate,
              item_code: row.add_descr_4, // ✅ 추가
            });
          }
        });
        if (info.length === 0) {
          setSnackbar({
            open: true,
            message: "완료 수량이 입력된 주문이 없습니다",
            severity: "warning",
          });
          return;
        }
      }
  
      await axios.post(`${TPC_URL}/api/tpc/postOrderResults`, {
        group_code,
        project_code,
        direction,
        info,
      });
  
      await fetchData();
      setRowSelection({});
      setCompletedQuantities({});
      setCompletedDates({});
      
      const baseDate = await fetchBaseDate();
      setBatchCompletedDate(dayjs(baseDate));
      
      setSnackbar({
        open: true,
        message: "완료 처리 및 재고 차감이 완료되었습니다",
        severity: "success",
      });
    } catch (e) {
      console.error("완료 처리 중 오류 발생:", e);
      setSnackbar({
        open: true,
        message: "완료 처리 중 오류가 발생했습니다",
        severity: "error",
      });
    }
  };
  
  const handleInputModeChange = async (event, newMode) => {
    if (newMode !== null) {
      setInputMode(newMode);
      setRowSelection({});
      setCompletedQuantities({});
      setCompletedDates({});
      
      // 모드 변경 시 기준 날짜 다시 설정
      const baseDate = await fetchBaseDate();
      setBatchCompletedDate(dayjs(baseDate));
      
      setSnackbar({
        open: true,
        message:
          newMode === "batch"
            ? "일괄 입력 모드로 전환되었습니다"
            : "개별 입력 모드로 전환되었습니다",
        severity: "info",
      });
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = showCompleted
        ? `/api/tpc/getProcessedDataEntire?group_code=${group_code}&project_code=${project_code}`
        : `/api/tpc/getProcessedData?group_code=${group_code}&project_code=${project_code}`;
      const response = await axios.get(TPC_URL + endpoint);

      if (response.data && Array.isArray(response.data)) {
        setRowData(response.data);
        setFilteredData(response.data);

        const dateMap = new Map();
        response.data.forEach((item) => {
          if (item.local_due_date) {
            const [year, month] = item.local_due_date.split("-");
            if (year && month) {
              if (!dateMap.has(year)) {
                dateMap.set(year, new Set());
              }
              dateMap.get(year).add(month);
            }
          }
        });

        const years = Array.from(dateMap.keys()).sort((a, b) => b - a);
        setYearOptions(years);
        setSelectedYear("");
        setSelectedMonth("");
        setSearchTerm("");
        setFilterActive(false);
      } else {
        setRowData([]);
        setFilteredData([]);
        setError("데이터 형식이 유효하지 않습니다");
      }
    } catch (err) {
      console.error("데이터 로딩 오류:", err);
      setError(`데이터를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      setRowData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, showCompleted]);

  useEffect(() => {
    if (selectedYear && rowData.length > 0) {
      const months = new Set();
      rowData.forEach((item) => {
        if (item.local_due_date) {
          const [year, month] = item.local_due_date.split("-");
          if (year === selectedYear && month) {
            months.add(month);
          }
        }
      });
      setMonthOptions(Array.from(months).sort());
    } else {
      setMonthOptions([]);
    }
    setSelectedMonth("");
  }, [selectedYear, rowData]);
  useEffect(() => {
    applyFilter();
  }, [showOnlyNew]); // applyFilter는 제외

  const applyFilter = useCallback(() => {
    if (!selectedYear && !selectedMonth && !searchTerm && !showOnlyNew) {
      setFilteredData(rowData);
      setFilterActive(false);
      setSnackbar({
        open: true,
        message: "모든 필터가 초기화되었습니다",
        severity: "info",
      });
      return;
    }
    let filtered = [...rowData];
    if (showOnlyNew) {
      filtered = filtered.filter((item) => item.is_new === 1);
    }

    if (selectedYear) {
      filtered = filtered.filter((item) => {
        if (!item.local_due_date) return false;
        const [year, month] = item.local_due_date.split("-");
        if (selectedMonth) {
          return year === selectedYear && month === selectedMonth;
        }
        return year === selectedYear;
      });
    }

    if (searchTerm) {
      const normalizedSearchTerm = searchTerm.replace(/\s+/g, "").toLowerCase();
      filtered = filtered.filter((item) => {
        const orderNumberMatch =
          item.order_number &&
          item.order_number.toLowerCase().includes(normalizedSearchTerm);
        const materialGroupingMatch =
          item.material_grouping &&
          item.material_grouping
            .replace(/\s+/g, "")
            .toLowerCase()
            .includes(normalizedSearchTerm);
        const addDescr4Match =
          item.add_descr_4 &&
          item.add_descr_4
            .replace(/\s+/g, "")
            .toLowerCase()
            .includes(normalizedSearchTerm);
        return orderNumberMatch || materialGroupingMatch || addDescr4Match;
      });
    }

    setFilteredData(filtered);
    setFilterActive(true);
    setSnackbar({
      open: true,
      message: "필터가 적용되었습니다",
      severity: "success",
    });
  }, [selectedYear, selectedMonth, searchTerm, rowData, showOnlyNew]);

  const clearFilter = useCallback(() => {
    setSelectedYear("");
    setSelectedMonth("");
    setSearchTerm("");
    setShowCompleted(false);
    setShowOnlyNew(false); 
    setFilteredData(rowData);
    setFilterActive(false);
    setRowSelection({}); 
    setSnackbar({
      open: true,
      message: "모든 필터가 초기화되었습니다",
      severity: "info",
    });
    fetchData();
  }, [rowData, fetchData]);
  const handleNewOrderToggle = useCallback(() => {
    const newValue = !showOnlyNew;
    
    setShowOnlyNew(newValue);
    setRowSelection({}); // ✅ 선택 초기화
    
    if (newValue) {
      setSnackbar({
        open: true,
        message: "새로운 주문만 표시합니다.",
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: "모든 주문을 표시합니다.",
        severity: "success",
      });
    }
  }, [showOnlyNew]);

  const handleCompletedToggle = useCallback(() => {
    setRowSelection({});
    if (!showCompleted) {
      setSnackbar({
        open: true,
        message: "완료 주문이 포함된 전체 주문을 표시합니다.",
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: "미완료된 주문만을 표시합니다.",
        severity: "success",
      });
    }
    setShowCompleted(!showCompleted);
  }, [showCompleted]);

  useEffect(() => {
    fetchData();
  }, [fetchData, showCompleted]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              납품 계획 현황
            </Typography>

            {/* <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={handleInputModeChange}
              size="small"
              sx={{ height: 40 }}
            >
              <ToggleButton value="batch">일괄 입력</ToggleButton>
              <ToggleButton value="individual">개별 입력</ToggleButton>
            </ToggleButtonGroup>
 */}
            {inputMode === "batch" && (
              <>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="완료 일시"
                    value={batchCompletedDate}
                    onChange={(newValue) => setBatchCompletedDate(newValue)}
                    format="YYYY/MM/DD"
                    disabled={selectedRows.length === 0}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: 180, height: 40 },
                      },
                    }}
                  />
                </LocalizationProvider>
                {selectedRows.length > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleCompleteOrders}
                    sx={{ height: 40 }}
                  >
                    완료 처리 ({selectedRows.length})
                  </Button>
                )}
              </>
            )}

            {inputMode === "individual" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleCompleteOrders}
                sx={{ height: 40 }}
              >
                완료 처리
              </Button>
            )}
          </ToolbarLeft>

          <ToolbarRight>
          <FormControlLabel
              control={
                <Switch
                  checked={showOnlyNew}
                  onChange={handleNewOrderToggle}
                  color="error"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {showOnlyNew ? "NEW 주문만 보는중" : "전체 주문 보기 중"}
                  {showOnlyNew && (
                    <Chip 
                      label="NEW" 
                      size="small" 
                      color="error"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ height: 40, my: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showCompleted}
                  onChange={handleCompletedToggle}
                  color="primary"
                />
              }
              label={
                showCompleted ? "전체 주문 보기 중" : "미완료 주문만 보는중"
              }
              sx={{ height: 40, my: 0 }}
            />
            <FormControl variant="outlined" size="small" sx={{ height: 40 }}>
              <TextField
                placeholder="검색어를 입력하세요."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ color: "#777", mr: 1 }}
                      fontSize="small"
                    />
                  ),
                  endAdornment: searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{ width: 200, height: 40 }}
              />
            </FormControl>
            <FilterContainer>
              <FormControl size="small" sx={{ minWidth: 100, height: 40 }}>
                <InputLabel id="year-select-label">연도</InputLabel>
                <Select
                  labelId="year-select-label"
                  value={selectedYear}
                  label="연도"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">
                    <em>전체</em>
                  </MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 80, height: 40 }}>
                <InputLabel id="month-select-label">월</InputLabel>
                <Select
                  labelId="month-select-label"
                  value={selectedMonth}
                  label="월"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={!selectedYear}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">
                    <em>전체</em>
                  </MenuItem>
                  {monthOptions.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={applyFilter}
                sx={{ height: 40, minWidth: 60 }}
              >
                적용
              </Button>
              {filterActive && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={clearFilter}
                  sx={{ height: 40, minWidth: 60 }}
                >
                  초기화
                </Button>
              )}
            </FilterContainer>
          </ToolbarRight>
        </ToolbarContainer>

        <InnerContainer>
          {loading ? (
            <LoadingContainer>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                데이터를 불러오는 중...
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <LoadingContainer>
              <Typography variant="h6" color="error">
                {error}
              </Typography>
            </LoadingContainer>
          ) : (
<MaterialReactTable
  columns={columns}
  data={filteredData}
  localization={MRT_Localization_KO}
  enableRowSelection={(row) => {
    if (inputMode === "batch") {
      return row.original.status !== 1; 
    }
    return false;
  }}
  enableMultiRowSelection={inputMode === "batch"}
  onRowSelectionChange={setRowSelection}
  state={{ rowSelection }}
  getRowId={(row) => row.order_number}
  enableColumnActions={false}
  enableColumnFilters={false}
  enablePagination={true}
  enableSorting={true}
  enableBottomToolbar={true}
  enableTopToolbar={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableHiding={false}
  muiTableBodyRowProps={({ row }) => {
    const noDueDate =
      row.original.local_due_date === null ||
      row.original.local_due_date === undefined ||
      row.original.local_due_date === "" ||
      row.original.local_due_date === "null";
    
    // ✅ status가 1이면 완료된 주문
    const isCompleted = row.original.status === 1;
  
    let bgColor = "white";
    let hoverColor = "#f5f5f5";
  
    if (isCompleted) {
      bgColor = "#fff9c4"; // 노란색 (완료)
      hoverColor = "#fff59d";
    } else if (noDueDate) {
      bgColor = "#f5f5f5"; // 회색 (납기일 없음)
      hoverColor = "#e0e0e0";
    }
  
    return {
      sx: {
        bgcolor: bgColor,
        "&:hover": {
          bgcolor: hoverColor,
        },
      },
    };
  }}
    muiTableProps={{
    sx: {
      tableLayout: "fixed",
    },
  }}
  // ... 나머지 props
/>
          )}
        </InnerContainer>
      </FrameContainer>

      {/* 납품 실적 내역 모달 */}
      <Dialog
        open={historyOpen}
        onClose={handleCloseHistory}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          주문 내역 보기{" "}
          {historyOrderNumber ? `- 주문번호 ${historyOrderNumber}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress />
            </Box>
          ) : historyError ? (
            <Typography color="error">{historyError}</Typography>
          ) : historyRows.length === 0 ? (
            <Typography>해당 주문에 대한 납품 실적이 없습니다.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">주문번호</TableCell>
                  <TableCell align="center">납품 수량</TableCell>
                  <TableCell align="center">납품 일시</TableCell>
                  <TableCell align="center">등록 일시</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.map((row) => (
                  <TableRow key={row.result_id}>
                    <TableCell align="center">{row.order_number}</TableCell>
                    <TableCell align="center">
                      {row.delivery_qty?.toLocaleString?.() ?? row.delivery_qty}
                    </TableCell>
                    <TableCell align="center">{row.delivery_date}</TableCell>
                    <TableCell align="center">
                      {row.register_timestamp}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default Processed;
